/* Preserve the verified canonical account. Member-price display requires both
 * its current benefit grant and a matching canonical-source price response
 * whose memberPriceEligible is true. */
(() => {
  'use strict';

  if (window.__PK_VIP_ENTITLEMENT_BRIDGE__) return;
  window.__PK_VIP_ENTITLEMENT_BRIDGE__ = true;
  const club = window.PKPupClub;
  if (!club || club.transport() !== 'supabase') return;

  const originalAccountState = club.accountState;
  const sessionKey = 'pk-pup-club-session-v2';
  let accountSequence = 0;

  function staleSession() {
    const error = new Error('stale_session');
    error.stale = true;
    return error;
  }

  function cachedAccessToken() {
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        const storage = window[name];
        const raw = storage && storage.getItem(sessionKey);
        const record = raw ? JSON.parse(raw) : null;
        const token = record && record.accessToken;
        if (typeof token === 'string' && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) return token;
      } catch { /* No stored token means member-price confirmation stays closed. */ }
    }
    return null;
  }

  function canonicalBenefits(member) {
    return !!(member && !Array.isArray(member)
      && member.entitlementAuthority === 'supabase_membership_v2'
      && member.member === true && member.portalAccess === true
      && member.stateVerified === true && member.testFixture !== true
      && member.benefitsActive === true && member.discount === 'member'
      && ['trialing', 'active'].includes(member.status));
  }

  /* Isolated merchant review fixture: the client's /user-verified review token
   * and every server fixture indicator must agree. A labeled test state, never a
   * provider-confirmed benefit. Query strings and user_metadata cannot reach it. */
  function reviewFixture(account, member) {
    return !!(account.reviewAccount === true && typeof club.isReviewAccount === 'function'
      && club.isReviewAccount() === true
      && member && typeof member === 'object' && !Array.isArray(member)
      && member.entitlementAuthority === 'merchant_review_isolated'
      && member.testAccount === true && member.testFixture === true
      && member.member === true && member.portalAccess === true
      && member.stateVerified === true
      && ['active', 'paused', 'canceled'].includes(member.status));
  }

  async function confirmMemberPrice(accessToken, status) {
    const restUrl = String(window.PK_PUP_CLUB && window.PK_PUP_CLUB.restUrl || '');
    const endpoint = restUrl.replace(/\/rest\/v1\/?$/, '') + '/functions/v1/shopify-member-entitlement';
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 8000) : null;
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: window.PK_PUP_CLUB.anonKey,
          Authorization: 'Bearer ' + accessToken,
        },
        body: '{}',
        credentials: 'omit',
        ...(controller ? { signal: controller.signal } : {}),
      });
      const body = await response.json().catch(() => null);
      /* 2026-10-09 release-5: shopify-member-entitlement v10 contract. Member
         prices and the member checkout code follow memberPriceEligible === true
         (a verified, trialing or active, Phoenix-synced member), never a
         client-side reading of the other fields alone. */
      return !!(response.ok && body && !Array.isArray(body)
        && body.memberPriceEligible === true
        && body.authenticated === true && body.source === 'supabase_v2'
        && body.portalAccess === true && body.benefitsActive === true
        && body.discount === 'member' && body.status === status);
    } catch {
      return false;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  club.accountState = async () => {
    const sequence = ++accountSequence;
    const original = await originalAccountState();
    if (sequence !== accountSequence) throw staleSession();

    // A retained envelope or indeterminate /user response never promotes auth.
    if (!original || original.authenticated !== true || original.authenticationIndeterminate === true
        || (original.auth && original.auth.indeterminate === true)) return original;
    const revision = original.sessionRevision;
    if (typeof club.sessionRevision !== 'function' || typeof club.sessionIsCurrent !== 'function'
        || !club.signedIn() || revision !== club.sessionRevision()) throw staleSession();

    const membership = original.membership;
    const status = String(membership && membership.status || 'unknown').toLowerCase();
    const account = { ...original, entitlement: { status, benefitsActive: false } };
    // The verified merchant review identity never reaches the Shopify provider.
    if (original.reviewAccount === true || (typeof club.isReviewAccount === 'function' && club.isReviewAccount())) {
      if (!reviewFixture(original, membership)) return account;
      const fixtureActive = membership.status === 'active' && membership.benefitsActive === true
        && membership.discount === 'member';
      return { ...account, entitlement: { status, benefitsActive: fixtureActive, testFixture: true,
        authority: 'merchant_review_isolated' } };
    }
    if (!canonicalBenefits(membership)) return account;

    const accessToken = cachedAccessToken();
    if (!accessToken) return account;
    if (!club.sessionIsCurrent(accessToken, revision)) throw staleSession();
    const benefitsActive = await confirmMemberPrice(accessToken, status);
    if (sequence !== accountSequence || !club.sessionIsCurrent(accessToken, revision)) throw staleSession();
    return { ...account, entitlement: { status, benefitsActive } };
  };
})();
